/**
 * BuddyGroupMemberList — Display members of a buddy group with status badges.
 *
 * Shows accepted members and pending invitations with visual distinction.
 * Primary cardholder can remove any member (pending or accepted).
 * A confirmation dialog is shown before removing/cancelling.
 */

import { useState } from 'react';
import { User, Crown, X, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import type { BuddyGroupMemberView } from '@/types/buddy-groups';

interface ConfirmTarget {
  memberId: string;
  displayName: string;
  isPending: boolean;
}

interface BuddyGroupMemberListProps {
  members: BuddyGroupMemberView[];
  currentUserProfileId: string;
  isPrimaryUser: boolean;
  onRemoveMember?: (memberId: string) => void;
  removingMemberId?: string | null;
}

export function BuddyGroupMemberList({
  members,
  currentUserProfileId,
  isPrimaryUser,
  onRemoveMember,
  removingMemberId,
}: BuddyGroupMemberListProps) {
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  if (!members.length) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No members in this group yet.
      </p>
    );
  }

  // Sort: primary first, then accepted members alphabetically, then pending alphabetically
  const sorted = [...members].sort((a, b) => {
    // Primary always first
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    // Accepted before pending
    const aPending = a.invitation_status === 'pending';
    const bPending = b.invitation_status === 'pending';
    if (!aPending && bPending) return -1;
    if (aPending && !bPending) return 1;
    // Alphabetical within same group
    return (a.display_name ?? '').localeCompare(b.display_name ?? '');
  });

  return (
    <>
    <ul className="divide-y">
      {sorted.map((member) => {
        const isCurrentUser = member.user_profile_id === currentUserProfileId;
        const displayName =
          member.display_name ?? member.user_profile_id.slice(0, 8);
        const isRemoving = removingMemberId === member.id;
        const isPending = member.invitation_status === 'pending';

        return (
          <li
            key={member.id}
            className={`flex items-center justify-between py-3 ${
              isPending ? 'bg-orange-500/10 -mx-2 px-2 rounded-md' : ''
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                isPending ? 'bg-muted/50 border border-dashed border-muted-foreground/30' : 'bg-muted'
              }`}>
                {member.is_primary ? (
                  <Crown className="h-4 w-4 text-amber-500" />
                ) : isPending ? (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <User className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {displayName}
                  </span>
                  {isCurrentUser && (
                    <span className="text-[10px] text-muted-foreground">
                      (you)
                    </span>
                  )}
                  {member.is_primary && !isCurrentUser && (
                    <span className="text-[10px] text-amber-500">
                      (primary cardholder)
                    </span>
                  )}
                  {isPending && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 h-4 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30"
                    >
                      pending
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  {member.member_number && (
                    <span>#{member.member_number}</span>
                  )}
                  {/* Hide email for pending invitees — accepting the
                      invitation is the trust gesture that opts them
                      into email visibility for the group. */}
                  {!isPending && member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="truncate hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {member.email}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Remove button — primary can remove others (pending or accepted) */}
            {isPrimaryUser &&
              !isCurrentUser &&
              !member.is_primary &&
              onRemoveMember && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive h-7 w-7 p-0"
                  disabled={isRemoving}
                  onClick={() =>
                    setConfirmTarget({
                      memberId: member.id,
                      displayName,
                      isPending,
                    })
                  }
                  title={isPending ? `Cancel invitation for ${displayName}` : `Remove ${displayName}`}
                >
                  {isRemoving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
          </li>
        );
      })}
    </ul>

    {/* Confirmation dialog for remove / cancel invitation */}
    <Dialog
      open={confirmTarget !== null}
      onOpenChange={(open) => {
        if (!open) setConfirmTarget(null);
      }}
    >
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>
            {confirmTarget?.isPending
              ? 'Cancel Invitation'
              : 'Remove Member'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {confirmTarget?.isPending
            ? `Cancel invitation for ${confirmTarget.displayName}?`
            : `Remove ${confirmTarget?.displayName} from this group? They will be notified.`}
        </p>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => setConfirmTarget(null)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (confirmTarget && onRemoveMember) {
                onRemoveMember(confirmTarget.memberId);
              }
              setConfirmTarget(null);
            }}
          >
            {confirmTarget?.isPending ? 'Confirm' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
